
import React, { useEffect, useRef } from 'react';
import { Ticket, LabelSize } from '../types'; // Added LabelSize
import { DEFAULT_LABEL_SIZE_ID, DYMO_LABEL_SIZES } from '../constants'; // Added DEFAULT_LABEL_SIZE_ID

// Note: JsBarcode is loaded via CDN in index.html
declare var JsBarcode: any;

interface PrintableDeviceLabelProps {
  ticket: Ticket;
  labelSizeId?: string; // Optional: if not provided, uses default
}

const PREVIEW_DPI = 96; // Standard screen DPI

export const PrintableDeviceLabel: React.FC<PrintableDeviceLabelProps> = ({ ticket, labelSizeId = DEFAULT_LABEL_SIZE_ID }) => {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const selectedLabelSize = DYMO_LABEL_SIZES.find(size => size.id === labelSizeId) || DYMO_LABEL_SIZES[0];
  const { widthInches, heightInches } = selectedLabelSize;

  useEffect(() => {
    if (ticket && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, ticket.id, {
          format: "CODE128",
          width: 1.5, // Adjust barcode width relative to label size if needed
          height: Math.min(40, heightInches * PREVIEW_DPI * 0.3), // Barcode height relative to label, max 40px
          displayValue: true,
          fontSize: 8, // Smaller font for barcode value for small labels
          textMargin: 0,
          margin: 2,
        });
      } catch (e) {
        console.error("Barcode generation failed for device label:", e);
        if (barcodeRef.current) {
            barcodeRef.current.innerHTML = '<text x="10" y="20" fill="red" font-size="8">Error</text>';
        }
      }
    }
  }, [ticket, widthInches, heightInches]);

  if (!ticket) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA'); // YYYY-MM-DD
  };

  const labelStyle: React.CSSProperties = {
    width: `${widthInches * PREVIEW_DPI}px`,
    height: `${heightInches * PREVIEW_DPI}px`,
    fontSize: '8pt', // Keep base font small for labels
    border: '1px dashed #9ca3af',
    padding: '0.1in',
    boxSizing: 'border-box',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    color: 'black',
    lineHeight: '1.2',
  };
  
  const maxBarcodeHeight = heightInches * PREVIEW_DPI * 0.4; // Allow up to 40% of label height for barcode

  return (
    <div style={labelStyle} className="font-sans">
      <div>
        <h1 style={{ fontSize: Math.min(10, widthInches * 3) + 'pt' }} className="font-bold text-center mb-1">TECH MEDICS</h1>
        <div className="grid grid-cols-2 gap-x-1 items-start">
          <div className="overflow-hidden">
            <p className="font-semibold">TID: <span className="font-normal">{ticket.id}</span></p>
            <p className="font-semibold truncate" title={ticket.customer}>Cust: <span className="font-normal">{ticket.customer}</span></p>
            <p className="font-semibold truncate" title={ticket.deviceModel}>Device: <span className="font-normal">{ticket.deviceModel || 'N/A'}</span></p>
            {ticket.deviceSerialNumber && <p className="font-semibold truncate">S/N: <span className="font-normal">{ticket.deviceSerialNumber}</span></p>}
          </div>
          <div className="flex justify-center items-center" style={{ height: `${maxBarcodeHeight}px` }}>
            <svg ref={barcodeRef} className="max-w-full max-h-full"></svg>
          </div>
        </div>
      </div>
      <div className="text-right mt-auto">
        <p style={{ fontSize: '7pt' }}>Intake: {formatDate(ticket.createdAt)}</p>
      </div>
    </div>
  );
};
